import { useState } from 'react'
import { CfoChartFrame, useChartTooltip } from './chartInteraction'
import { formatCurrencyCompact } from '../cfoFormat'
import { spreadVertically } from '../visuals/labels'

export type PaceCurveDay = {
  date: string
  day_index: number
  actual_cum?: number | null
  prior_year_cum?: number | null
  plan_cum?: number | null
  forecast_cum?: number | null
  is_future: boolean
}

export type PaceCurveProjection = {
  end?: number | null
  low?: number | null
  high?: number | null
}

export type PaceCurveProps = {
  days: PaceCurveDay[]
  projection?: PaceCurveProjection | null
  basis: 'plan' | 'forecast' | 'prior_year' | 'prior_period'
  todayIndex?: number | null
  ariaLabel: string
  formatValue?: (value: number) => string
  width?: number
  height?: number
  className?: string
}

export type PacePoint = { x: number; y: number; value: number; index: number }

export type PaceGeometry = {
  min: number
  max: number
  actual: PacePoint[]
  actualSegments: PacePoint[][]
  basis: PacePoint[]
  priorYear: PacePoint[]
  projection: PacePoint[]
  cone: { x: number; yLow: number; yHigh: number }[]
  ticks: Array<{ index: number; label: string; x: number }>
  todayX: number | null
  toX: (index: number) => number
  toY: (value: number) => number
}

const BASIS_KEY: Record<PaceCurveProps['basis'], 'plan_cum' | 'forecast_cum' | 'prior_year_cum'> = {
  plan: 'plan_cum',
  forecast: 'forecast_cum',
  prior_year: 'prior_year_cum',
  prior_period: 'prior_year_cum',
}

/** Domain includes 0; nulls break the actual line; projection starts at last actual. */
export function computePaceGeometry(
  days: PaceCurveDay[],
  projection: PaceCurveProjection | null | undefined,
  basis: PaceCurveProps['basis'],
  width: number,
  height: number,
  todayIndex?: number | null,
): PaceGeometry {
  const padTop = 18
  const padBottom = 28
  const padLeft = 36
  // Keep a real label rail on the right. Endpoint labels used to be drawn on
  // top of the final marks, so close plan / projection / prior-year values
  // became one unreadable knot.
  const padRight = 112
  const plotW = Math.max(1, width - padLeft - padRight)
  const plotH = Math.max(1, height - padTop - padBottom)
  const lastIndex = Math.max(0, days.length - 1)
  const toX = (index: number) => padLeft + (lastIndex === 0 ? 0 : (index / lastIndex) * plotW)

  const values: number[] = [0]
  for (const day of days) {
    if (day.actual_cum != null && Number.isFinite(day.actual_cum)) values.push(day.actual_cum)
    if (day.prior_year_cum != null && Number.isFinite(day.prior_year_cum)) values.push(day.prior_year_cum)
    const basisVal = day[BASIS_KEY[basis]]
    if (basisVal != null && Number.isFinite(basisVal)) values.push(basisVal)
  }
  if (projection?.end != null && Number.isFinite(projection.end)) values.push(projection.end)
  if (projection?.low != null && Number.isFinite(projection.low)) values.push(projection.low)
  if (projection?.high != null && Number.isFinite(projection.high)) values.push(projection.high)

  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const toY = (value: number) => padTop + (1 - (value - min) / span) * plotH

  const actual: PacePoint[] = []
  const actualSegments: PacePoint[][] = []
  let segment: PacePoint[] = []
  days.forEach((day, index) => {
    const value = day.actual_cum
    if (value == null || !Number.isFinite(value) || day.is_future) {
      if (segment.length) actualSegments.push(segment)
      segment = []
      return
    }
    const point = { x: toX(index), y: toY(value), value, index }
    actual.push(point)
    segment.push(point)
  })
  if (segment.length) actualSegments.push(segment)

  const series = (key: 'plan_cum' | 'forecast_cum' | 'prior_year_cum'): PacePoint[] =>
    days.flatMap((day, index) => {
      const value = day[key]
      if (value == null || !Number.isFinite(value)) return []
      return [{ x: toX(index), y: toY(value), value, index }]
    })

  const lastActual = actual[actual.length - 1] || null
  const projectionPoints: PacePoint[] = []
  const cone: PaceGeometry['cone'] = []
  if (lastActual && projection?.end != null && Number.isFinite(projection.end)) {
    projectionPoints.push(lastActual)
    projectionPoints.push({ x: toX(lastIndex), y: toY(projection.end), value: projection.end, index: lastIndex })
    const low = projection.low ?? projection.end
    const high = projection.high ?? projection.end
    cone.push({ x: lastActual.x, yLow: lastActual.y, yHigh: lastActual.y })
    cone.push({ x: toX(lastIndex), yLow: toY(low), yHigh: toY(high) })
  }

  const tickIndexes = ticksForDays(days)
  const ticks = tickIndexes.map((index) => ({
    index,
    label: String(days[index]?.day_index ?? index + 1),
    x: toX(index),
  }))

  const resolvedToday = todayIndex != null
    ? todayIndex
    : days.reduce((last, day, index) => (!day.is_future ? index : last), -1)

  return {
    min,
    max,
    actual,
    actualSegments,
    basis: series(BASIS_KEY[basis]),
    priorYear: series('prior_year_cum'),
    projection: projectionPoints,
    cone,
    ticks,
    todayX: resolvedToday >= 0 ? toX(resolvedToday) : null,
    toX,
    toY,
  }
}

export function ticksForDays(days: PaceCurveDay[]): number[] {
  if (days.length <= 10) return days.map((_, index) => index)
  const preferred = [1, 8, 15, 22, 29]
  const monthHits = preferred
    .map((dayIndex) => days.findIndex((day) => day.day_index === dayIndex))
    .filter((index) => index >= 0)
  if (days.length >= 28 && days.length <= 31 && monthHits.length >= 3) {
    return unique([0, ...monthHits, days.length - 1].filter((index) => index < days.length))
  }
  const weekStarts = days
    .map((day, index) => ({ day, index }))
    .filter(({ day }) => {
      const date = new Date(`${day.date}T00:00:00`)
      return !Number.isNaN(date.getTime()) && date.getDay() === 1
    })
    .map(({ index }) => index)
  if (weekStarts.length >= 3) return unique([0, ...weekStarts, days.length - 1])
  return unique([0, ...monthHits, days.length - 1])
}

function unique(values: number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b)
}

function polyline(points: PacePoint[]): string {
  return points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')
}

export function PaceCurve({
  days,
  projection,
  basis,
  todayIndex,
  ariaLabel,
  formatValue,
  width = 720,
  height = 240,
  className,
}: PaceCurveProps) {
  const { containerRef, tip, show, hide } = useChartTooltip()
  const [hover, setHover] = useState<number | null>(null)
  const geo = computePaceGeometry(days, projection, basis, width, height, todayIndex)
  const fmt = formatValue || ((value: number) => formatCurrencyCompact(value))
  const lastActual = geo.actual[geo.actual.length - 1]
  const lastBasis = geo.basis[geo.basis.length - 1]
  const lastPy = geo.priorYear[geo.priorYear.length - 1]
  const lastProj = geo.projection[geo.projection.length - 1]
  const basisLabel = basis === 'plan' ? 'plan' : basis === 'forecast' ? 'forecast' : basis === 'prior_year' ? 'prior year' : 'prior period'
  const actualAtEndpoint = lastActual?.index === days.length - 1
  const endpointLabels = [
    actualAtEndpoint && lastActual
      ? { key: 'actual', point: lastActual, text: `actual ${fmt(lastActual.value)}`, color: 'var(--ink, #1c1f26)', weight: 600 }
      : null,
    lastBasis
      ? { key: 'basis', point: lastBasis, text: `${basisLabel} ${fmt(lastBasis.value)}`, color: 'var(--brand, #1b4965)', weight: 400 }
      : null,
    lastPy && basis !== 'prior_year' && basis !== 'prior_period'
      ? { key: 'prior-year', point: lastPy, text: `prior year ${fmt(lastPy.value)}`, color: 'var(--anchor, #9aa0a8)', weight: 400 }
      : null,
    lastProj
      ? { key: 'projection', point: lastProj, text: `on pace ${fmt(lastProj.value)}`, color: 'var(--ink, #1c1f26)', weight: 400 }
      : null,
  ].filter((entry): entry is NonNullable<typeof entry> => entry != null)
  const endpointLabelYs = spreadVertically(
    endpointLabels.map((entry) => ({ key: entry.key, y: entry.point.y + 3 })),
    { minGap: 14, top: 14, bottom: height - 38 },
  )

  const conePath = geo.cone.length === 2
    ? `M ${geo.cone[0].x} ${geo.cone[0].yHigh} L ${geo.cone[1].x} ${geo.cone[1].yHigh} L ${geo.cone[1].x} ${geo.cone[1].yLow} L ${geo.cone[0].x} ${geo.cone[0].yLow} Z`
    : ''

  return (
    <CfoChartFrame containerRef={containerRef} tip={tip}>
      <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        {geo.ticks.map((tick) => (
          <g key={`tick-${tick.index}`}>
            <line x1={tick.x} y1={18} x2={tick.x} y2={height - 28} stroke="var(--hair, #e7e2d6)" strokeWidth={1} />
            <text x={tick.x} y={height - 10} textAnchor="middle" fill="var(--muted, #868c9a)" fontSize={10}>{tick.label}</text>
          </g>
        ))}
        {geo.todayX != null ? (
          <line x1={geo.todayX} y1={14} x2={geo.todayX} y2={height - 28} stroke="var(--brand, #1b4965)" strokeWidth={1.4} />
        ) : null}
        {conePath ? <path d={conePath} fill="var(--brand, #1b4965)" opacity={0.08} /> : null}
        {geo.priorYear.length > 1 ? (
          <polyline fill="none" stroke="var(--anchor, #9aa0a8)" strokeWidth={1.6} strokeDasharray="4 4" points={polyline(geo.priorYear)} />
        ) : null}
        {geo.basis.length > 1 ? (
          <polyline fill="none" stroke="var(--brand, #1b4965)" strokeWidth={1.8} points={polyline(geo.basis)} />
        ) : null}
        {geo.projection.length > 1 ? (
          <polyline fill="none" stroke="var(--ink, #1c1f26)" strokeWidth={1.8} strokeDasharray="3 4" points={polyline(geo.projection)} />
        ) : null}
        {geo.actualSegments.map((segment, index) => (
          <polyline key={`actual-${index}`} fill="none" stroke="var(--ink, #1c1f26)" strokeWidth={2.4} strokeLinecap="round" points={polyline(segment)} />
        ))}
        {lastActual && !actualAtEndpoint ? (
          <text x={lastActual.x + 6} y={lastActual.y - 6} fill="var(--ink, #1c1f26)" fontSize={11} fontWeight={600}>{fmt(lastActual.value)}</text>
        ) : null}
        {endpointLabels.map((entry) => {
          const labelY = endpointLabelYs.get(entry.key) ?? entry.point.y + 3
          return (
            <g key={`end-label-${entry.key}`}>
              <line
                x1={entry.point.x + 3}
                y1={entry.point.y}
                x2={entry.point.x + 10}
                y2={labelY - 3}
                stroke={entry.color}
                strokeWidth={1}
                opacity={0.7}
              />
              <text
                x={width - 8}
                y={labelY}
                textAnchor="end"
                fill={entry.color}
                fontSize={10}
                fontWeight={entry.weight}
              >
                {entry.text}
              </text>
            </g>
          )
        })}
        {days.map((day, index) => (
          <rect
            key={day.date}
            x={geo.toX(index) - 6}
            y={14}
            width={12}
            height={height - 42}
            fill="transparent"
            onMouseEnter={(event) => {
              setHover(index)
              const actual = day.actual_cum
              show(event.clientX, event.clientY, actual == null ? 'not yet' : fmt(actual), day.date)
            }}
            onMouseLeave={() => {
              setHover(null)
              hide()
            }}
          />
        ))}
        {hover != null && days[hover]?.actual_cum != null ? (
          <circle cx={geo.toX(hover)} cy={geo.toY(days[hover].actual_cum as number)} r={3.2} fill="var(--ink, #1c1f26)" />
        ) : null}
      </svg>
    </CfoChartFrame>
  )
}

export default PaceCurve
